import { Logo, Typography } from "@/components/atoms";
import { BackArrowIcon } from "@/components/atoms/BackArrowIcon";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CircleUserRoundIcon,
  CopyWebIcon,
} from "@/components/atoms/icons";
import { Toast } from "@/components/molecules";
import { useIsAdhaJourney, useTranslation, useUIColors, useWebViewport } from "@/core/hooks";
import { getWebHeaderHorizontalPadding } from "@/core/constants/webLayout";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useAppStore, type User } from "@/core/stores/appStore";
import { getUserFirstName, getUserFullName, hasTextValue } from "@/core/utils";
import { BREAKPOINTS } from "@/core/utils/responsive";
import * as Clipboard from "expo-clipboard";
import { router, useNavigation, usePathname } from "expo-router";
import React, { useEffect, useRef, useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import { UserProfileDropdown } from "./UserProfileDropdown";

export interface BreadcrumbItem {
  readonly label: string;
  readonly path?: string;
  /**
   * Kept for callers. Breadcrumb clicks always open `path`
   * (they do not replace or go back in browser history).
   */
  readonly replace?: boolean;
}

export interface WebHeaderProps {
  readonly title: string;
  readonly breadcrumbs?: BreadcrumbItem[];
  readonly showBackButton?: boolean;
  readonly onBackPress?: () => void;
  /**
   * When true (ADHA / hideSidebar), logo aligns with page content edge
   * (shell + WEB_PAGE_CONTENT_PADDING). Otherwise logo aligns with shell only.
   */
  readonly alignWithPageContent?: boolean;
}

function getWebHeaderProfileSubtitle(
  isAdhaJourney: boolean,
  adhaApplicationNumber: string | null | undefined,
  userEmail: string,
): string {
  if (!isAdhaJourney) {
    return userEmail;
  }
  return hasTextValue(adhaApplicationNumber) ? adhaApplicationNumber : "";
}

const WEB_HEADER_TAB_ROUTES = new Set([
  "/(auth)/(home)",
  "/(auth)/(home)/index",
  "/(auth)/(home)/properties",
  "/(auth)/(home)/payments",
  "/home",
  "/properties",
  "/properties-web",
  "/payments",
]);

function isWebHeaderTabRoute(pathname: string): boolean {
  return WEB_HEADER_TAB_ROUTES.has(pathname.replace(/\/$/, ""));
}

function resolveNavigationCanGoBack(navigation: unknown): boolean {
  if (navigation == null || typeof navigation !== "object") {
    return false;
  }
  const canGoBack = (navigation as { canGoBack?: unknown }).canGoBack;
  if (typeof canGoBack !== "function") {
    return false;
  }
  return (canGoBack as () => boolean)();
}

function shouldShowWebHeaderBackButton(
  showBackButton: boolean,
  hasBreadcrumbs: boolean,
  isTabRoute: boolean,
  navCanGoBack: boolean,
): boolean {
  if (isTabRoute) {
    return false;
  }
  if (!showBackButton && !hasBreadcrumbs) {
    return false;
  }
  return hasBreadcrumbs || navCanGoBack;
}

function hasWebHeaderBreadcrumbs(
  breadcrumbs: readonly BreadcrumbItem[] | undefined,
): boolean {
  return Boolean(breadcrumbs && breadcrumbs.length > 0);
}

interface WebHeaderRouteLabels {
  readonly home: string;
  readonly properties: string;
  readonly propertyDetails: string;
  readonly payments: string;
  readonly profile: string;
}

function buildWebHeaderBreadcrumbs(
  pathname: string,
  breadcrumbs: readonly BreadcrumbItem[] | undefined,
  isTabRoute: boolean,
  labels: WebHeaderRouteLabels,
): BreadcrumbItem[] {
  if (isTabRoute) {
    return [];
  }
  if (breadcrumbs !== undefined) {
    return [...breadcrumbs];
  }

  const pathSegments = pathname
    .split("/")
    .filter(Boolean)
    .filter((segment) => segment !== "guest" && segment !== "guest-details");
  const routeLabels: Record<string, string> = {
    home: labels.home,
    properties: labels.properties,
    "property-details": labels.propertyDetails,
    payments: labels.payments,
    profile: labels.profile,
  };
  const generated: BreadcrumbItem[] = [];
  let currentPath = "";
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const label = routeLabels[segment] || segment;
    if (index < pathSegments.length - 1) {
      generated.push({ label, path: currentPath });
    } else {
      generated.push({ label });
    }
  });
  return generated;
}

function resolveWebHeaderDisplayName(
  user: User | null | undefined,
  isRTL: boolean,
  welcomeFallback: string,
): string {
  const fullName = getUserFullName(user, isRTL);
  if (fullName) {
    return fullName;
  }
  const firstName = getUserFirstName(user);
  if (firstName) {
    return firstName;
  }
  return welcomeFallback;
}

function resolveWebHeaderEmail(user: User | null | undefined): string {
  if (user?.primary_email) {
    return user.primary_email;
  }
  if (user?.secondary_email) {
    return user.secondary_email;
  }
  if (user?.tertiary_email) {
    return user.tertiary_email;
  }
  return "";
}

/** Matches the "body" Typography font size so the icon reads as part of the number. */
const COPY_ICON_SIZE = 16;
const COPY_ICON_HIDE_DELAY_MS = 120;

interface WebHeaderProfileSubtitleProps {
  readonly subtitle: string;
  readonly isAdhaJourney: boolean;
  readonly isRTL: boolean;
  readonly copyAccessibilityLabel: string;
  readonly copyIconColor: string;
  readonly onCopy: () => void;
}

function WebHeaderProfileSubtitle({
  subtitle,
  isAdhaJourney,
  isRTL,
  copyAccessibilityLabel,
  copyIconColor,
  onCopy,
}: Readonly<WebHeaderProfileSubtitleProps>) {
  const [isHovered, setIsHovered] = useState(false);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const showCopyIcon = () => {
    clearHideTimeout();
    setIsHovered(true);
  };

  // Moving between the number and the icon fires a hover-out before the next
  // hover-in, so the hide is deferred to keep the icon from flickering.
  const hideCopyIcon = () => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(
      () => setIsHovered(false),
      COPY_ICON_HIDE_DELAY_MS,
    );
  };

  useEffect(() => clearHideTimeout, []);

  if (!subtitle) {
    return null;
  }

  // Sonar S3358: avoid nested ternary in style object
  let paddingBottom = 6;
  if (isAdhaJourney) {
    paddingBottom = 0;
  } else if (isRTL) {
    paddingBottom = 3;
  }

  const text = (
    <Typography
      variant="body"
      color="secondary"
      numberOfLines={1}
      style={{
        textAlign: isRTL ? "right" : "left",
        paddingBottom,
      }}
    >
      {subtitle}
    </Typography>
  );

  if (!isAdhaJourney) {
    return text;
  }

  return (
    <View
      style={[
        styles.subtitleRow,
        {
          paddingBottom: isRTL ? 3 : 6,
          alignSelf: "flex-start",
        },
      ]}
    >
      <Pressable
        onHoverIn={showCopyIcon}
        onHoverOut={hideCopyIcon}
        accessibilityRole="none"
        accessible={false}
      >
        {text}
      </Pressable>
      <Pressable
        onHoverIn={showCopyIcon}
        onHoverOut={hideCopyIcon}
        onPress={(event) => {
          event.stopPropagation();
          onCopy();
        }}
        accessibilityRole="button"
        accessibilityLabel={copyAccessibilityLabel}
        focusable={isHovered}
        style={[styles.copyButton, !isHovered && styles.copyButtonHidden]}
      >
        <CopyWebIcon size={COPY_ICON_SIZE} color={copyIconColor} />
      </Pressable>
    </View>
  );
}

interface WebHeaderAnchorPosition {
  readonly x: number;
  readonly y: number;
}

interface WebHeaderChromeProps {
  readonly profileButtonPosition: WebHeaderAnchorPosition | null;
  readonly dropdownVisible: boolean;
  readonly onCloseDropdown: () => void;
  readonly toastVisible: boolean;
  readonly toastMessage: string;
  readonly onHideToast: () => void;
}

function WebHeaderChrome({
  profileButtonPosition,
  dropdownVisible,
  onCloseDropdown,
  toastVisible,
  toastMessage,
  onHideToast,
}: Readonly<WebHeaderChromeProps>) {
  return (
    <>
      {profileButtonPosition ? (
        <UserProfileDropdown
          visible={dropdownVisible}
          onClose={onCloseDropdown}
          anchorPosition={profileButtonPosition}
        />
      ) : null}
      <Toast
        visible={toastVisible}
        message={toastMessage}
        position="top-right"
        onHide={onHideToast}
      />
    </>
  );
}

interface WebHeaderBreadcrumbItemProps {
  readonly item: BreadcrumbItem;
  readonly showSeparator: boolean;
  readonly isRTL: boolean;
  readonly chevronSize: number;
  readonly chevronColor: string;
  readonly variant: "footnote" | "body";
  readonly onPress: (item: BreadcrumbItem) => void;
}

function WebHeaderBreadcrumbItem({
  item,
  showSeparator,
  isRTL,
  chevronSize,
  chevronColor,
  variant,
  onPress,
}: Readonly<WebHeaderBreadcrumbItemProps>) {
  return (
    <>
      {showSeparator ? (
        <View style={isRTL ? { transform: [{ scaleX: -1 }] } : undefined}>
          <ChevronRightIcon size={chevronSize} color={chevronColor} />
        </View>
      ) : null}
      <Pressable
        onPress={() => onPress(item)}
        disabled={!item.path}
        style={({ pressed }) => [item.path && pressed && { opacity: 0.7 }]}
      >
        <Typography
          variant={variant}
          color={item.path ? "secondary" : "primary"}
          style={item.path ? styles.breadcrumbLink : styles.breadcrumbText}
        >
          {item.label}
        </Typography>
      </Pressable>
    </>
  );
}

interface WebHeaderBreadcrumbRowProps {
  readonly items: readonly BreadcrumbItem[];
  readonly isRTL: boolean;
  readonly chevronSize: number;
  readonly chevronColor: string;
  readonly variant: "footnote" | "body";
  readonly onPress: (item: BreadcrumbItem) => void;
}

function WebHeaderBreadcrumbRow({
  items,
  isRTL,
  chevronSize,
  chevronColor,
  variant,
  onPress,
}: Readonly<WebHeaderBreadcrumbRowProps>) {
  if (items.length === 0) {
    return null;
  }
  return (
    <View style={[styles.breadcrumbs, { justifyContent: "flex-start" }]}>
      {items.map((item, index) => (
        <WebHeaderBreadcrumbItem
          key={item.path ?? `bc-${item.label}-${index}`}
          item={item}
          showSeparator={index > 0}
          isRTL={isRTL}
          chevronSize={chevronSize}
          chevronColor={chevronColor}
          variant={variant}
          onPress={onPress}
        />
      ))}
    </View>
  );
}

interface WebHeaderProfileChipProps {
  readonly showProfileDetails: boolean;
  readonly isAdhaJourney: boolean;
  readonly isMobile: boolean;
  readonly isRTL: boolean;
  readonly displayName: string;
  readonly profileSubtitle: string;
  readonly fontPrimary: string;
  readonly backgroundColor: string;
  readonly borderColor: string;
  readonly copyIconColor: string;
  readonly copyAccessibilityLabel: string;
  readonly profileButtonRef: React.RefObject<View | null>;
  readonly onProfilePress: () => void;
  readonly onCopy: () => void;
}

function WebHeaderAdhaProfileChip({
  isMobile,
  isRTL,
  displayName,
  profileSubtitle,
  fontPrimary,
  backgroundColor,
  borderColor,
  copyIconColor,
  copyAccessibilityLabel,
  profileButtonRef,
  onProfilePress,
  onCopy,
}: Readonly<Omit<WebHeaderProfileChipProps, "showProfileDetails" | "isAdhaJourney">>) {
  return (
    <View
      ref={profileButtonRef}
      style={[
        styles.profileButton,
        isMobile && styles.profileButtonMobile,
        { backgroundColor, borderColor },
      ]}
    >
      <View style={styles.profileContent}>
        <Pressable
          onPress={onProfilePress}
          accessibilityRole="button"
          accessibilityLabel={displayName}
        >
          <CircleUserRoundIcon size={isMobile ? 32 : 40} color={fontPrimary} />
        </Pressable>
        <View style={[styles.profileInfo, styles.profileInfoFlex]}>
          <Pressable
            onPress={onProfilePress}
            accessibilityRole="button"
            accessibilityLabel={displayName}
          >
            <Typography
              variant="title3"
              color="primary"
              numberOfLines={1}
              style={{ textAlign: isRTL ? "right" : "left", lineHeight: 24, paddingBottom: 2 }}
            >
              {displayName}
            </Typography>
          </Pressable>
          <WebHeaderProfileSubtitle
            subtitle={profileSubtitle}
            isAdhaJourney
            isRTL={isRTL}
            copyAccessibilityLabel={copyAccessibilityLabel}
            copyIconColor={copyIconColor}
            onCopy={onCopy}
          />
        </View>
        <Pressable
          onPress={onProfilePress}
          accessibilityRole="button"
          accessibilityLabel={displayName}
        >
          <ChevronDownIcon size={isMobile ? 20 : 24} color={fontPrimary} />
        </Pressable>
      </View>
    </View>
  );
}

function WebHeaderStandardProfileChip({
  showProfileDetails,
  isMobile,
  isRTL,
  displayName,
  profileSubtitle,
  fontPrimary,
  backgroundColor,
  borderColor,
  copyIconColor,
  copyAccessibilityLabel,
  profileButtonRef,
  onProfilePress,
  onCopy,
}: Readonly<Omit<WebHeaderProfileChipProps, "isAdhaJourney">>) {
  return (
    <Pressable
      ref={profileButtonRef}
      style={[
        styles.profileButton,
        isMobile && styles.profileButtonMobile,
        { backgroundColor, borderColor },
      ]}
      onPress={onProfilePress}
      accessibilityRole="button"
      accessibilityLabel={displayName}
    >
      <View style={styles.profileContent}>
        <CircleUserRoundIcon size={isMobile ? 32 : 40} color={fontPrimary} />
        {showProfileDetails ? (
          <View style={styles.profileInfo}>
            <Typography
              variant="title3"
              color="primary"
              numberOfLines={1}
              style={{ textAlign: isRTL ? "right" : "left" }}
            >
              {displayName}
            </Typography>
            <WebHeaderProfileSubtitle
              subtitle={profileSubtitle}
              isAdhaJourney={false}
              isRTL={isRTL}
              copyAccessibilityLabel={copyAccessibilityLabel}
              copyIconColor={copyIconColor}
              onCopy={onCopy}
            />
          </View>
        ) : null}
        <ChevronDownIcon size={isMobile ? 20 : 24} color={fontPrimary} />
      </View>
    </Pressable>
  );
}

function WebHeaderProfileChip(props: Readonly<WebHeaderProfileChipProps>) {
  if (props.showProfileDetails && props.isAdhaJourney) {
    return <WebHeaderAdhaProfileChip {...props} />;
  }
  return <WebHeaderStandardProfileChip {...props} />;
}

interface WebHeaderLayoutProps {
  readonly headerPadX: number;
  readonly title: string;
  readonly isRTL: boolean;
  readonly shouldShowBackButton: boolean;
  readonly onBackPress: () => void;
  readonly backIconColor: string;
  readonly profileButton: React.ReactNode;
  readonly breadcrumbRow: React.ReactNode;
  readonly chrome: React.ReactNode;
}

function WebHeaderDesktopLayout({
  headerPadX,
  title,
  isRTL,
  shouldShowBackButton,
  onBackPress,
  backIconColor,
  profileButton,
  breadcrumbRow,
  chrome,
}: Readonly<WebHeaderLayoutProps>) {
  const backButtonSpacing = isRTL ? { marginLeft: 12 } : { marginRight: 12 };
  return (
    <View
      style={[
        styles.desktopContainer,
        { paddingLeft: headerPadX, paddingRight: headerPadX },
      ]}
    >
      <View style={styles.topRow}>
        <View style={styles.desktopLeading}>
          <View style={styles.logoContainer}>
            <Logo color="bloomRed" size="large-medium" />
          </View>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {shouldShowBackButton ? (
              <Pressable
                onPress={onBackPress}
                style={[styles.backButton, backButtonSpacing]}
                accessibilityRole="button"
              >
                <BackArrowIcon size={34} color={backIconColor} />
              </Pressable>
            ) : null}
            <View style={[styles.centerSection, { gap: 4, justifyContent: "center" }]}>
              {title ? (
                <Typography
                  variant="title1"
                  color="primary"
                  style={[
                    styles.titleText,
                    { textAlign: isRTL ? "right" : "left", paddingTop: 0 },
                  ]}
                >
                  {title}
                </Typography>
              ) : null}
              {breadcrumbRow}
            </View>
          </View>
        </View>
        <View style={styles.desktopProfile}>{profileButton}</View>
      </View>
      {chrome}
    </View>
  );
}

function WebHeaderPhoneBreadcrumbLayout({
  headerPadX,
  phoneBreadcrumbBelow,
  profileButton,
  breadcrumbRow,
  chrome,
}: Readonly<
  Pick<
    WebHeaderLayoutProps,
    "headerPadX" | "profileButton" | "breadcrumbRow" | "chrome"
  > & { readonly phoneBreadcrumbBelow: boolean }
>) {
  return (
    <View
      style={[
        styles.header,
        styles.headerMobile,
        phoneBreadcrumbBelow && styles.headerMobileWithBreadcrumbBelow,
        { paddingLeft: headerPadX, paddingRight: headerPadX },
      ]}
    >
      <View style={styles.mobileLogoBreadcrumbRow}>
        <View style={styles.mobileLogoBreadcrumbColumn}>
          <View style={styles.logoContainerMobile}>
            <Logo color="bloomRed" size="medium" />
          </View>
          <View style={styles.mobileBreadcrumbRow}>{breadcrumbRow}</View>
        </View>
        <View style={[styles.rightSection, { flexDirection: "row" }]}>
          {profileButton}
        </View>
      </View>
      {chrome}
    </View>
  );
}

function WebHeaderCompactLayout({
  headerPadX,
  title,
  isPhone,
  shouldShowBackButton,
  onBackPress,
  backIconColor,
  backIconSize,
  titleVariant,
  profileButton,
  breadcrumbRow,
  chrome,
}: Readonly<
  Pick<
    WebHeaderLayoutProps,
    | "headerPadX"
    | "title"
    | "shouldShowBackButton"
    | "onBackPress"
    | "backIconColor"
    | "profileButton"
    | "breadcrumbRow"
    | "chrome"
  > & {
    readonly isPhone: boolean;
    readonly backIconSize: number;
    readonly titleVariant: "title2" | "largeTitle";
  }
>) {
  const logoSize = isPhone ? "medium" : "large-medium";
  return (
    <View
      style={[
        styles.header,
        styles.headerMobile,
        { paddingLeft: headerPadX, paddingRight: headerPadX },
      ]}
    >
      <View style={styles.mobileTopRow}>
        <View style={[styles.leftSection, { flexDirection: "row" }]}>
          <View
            style={[
              styles.logoContainerMobile,
              !isPhone && styles.logoContainer,
            ]}
          >
            <Logo color="bloomRed" size={logoSize} />
          </View>
          <View
            style={[
              styles.titleBreadcrumbContainer,
              styles.titleBreadcrumbContainerMobile,
            ]}
          >
            {shouldShowBackButton ? (
              <Pressable
                onPress={onBackPress}
                style={[styles.backButton, styles.backButtonMobile]}
                accessibilityRole="button"
              >
                <BackArrowIcon size={backIconSize} color={backIconColor} />
              </Pressable>
            ) : null}
            <View style={styles.centerSection}>
              {title ? (
                <Typography variant={titleVariant} color="primary" style={styles.titleText}>
                  {title}
                </Typography>
              ) : null}
              {breadcrumbRow}
            </View>
          </View>
        </View>
        <View style={[styles.rightSection, { flexDirection: "row" }]}>
          {profileButton}
        </View>
      </View>
      {chrome}
    </View>
  );
}

/**
 * Web header:
 * - Desktop: stacked — logo/profile on top; back + title + breadcrumbs below (title on top)
 * - Mobile: single row
 */
export function WebHeader({
  title,
  breadcrumbs,
  showBackButton = true,
  onBackPress,
  alignWithPageContent = false,
}: WebHeaderProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const pathname = usePathname();
  const navigation = useNavigation();
  const user = useAppStore((state) => state.auth.user);
  const adhaApplicationNumber = useAppStore(
    (state) => state.adhaLoanDetails?.adhaApplicationNumber,
  );
  const isAdhaJourney = useIsAdhaJourney();
  const { breakpointWidth } = useWebViewport();
  const isMobile = breakpointWidth < BREAKPOINTS.SHELL;
  /** Hide profile name/email only on phone; show on medium + desktop. */
  const isPhone = breakpointWidth < BREAKPOINTS.PHONE;
  const showProfileDetails = !isPhone;
  /** Same inset as WebAppLayout page content — logo tracks it at every size. */
  const headerPadX = getWebHeaderHorizontalPadding(isMobile, alignWithPageContent);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [profileButtonPosition, setProfileButtonPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const profileButtonRef = useRef<View>(null);

  const isTabRoute = isWebHeaderTabRoute(pathname);
  const navCanGoBack = resolveNavigationCanGoBack(navigation);
  const hasBreadcrumbs = hasWebHeaderBreadcrumbs(breadcrumbs);
  const shouldShowBackButton = shouldShowWebHeaderBackButton(
    showBackButton,
    hasBreadcrumbs,
    isTabRoute,
    navCanGoBack,
  );
  const autoBreadcrumbs = React.useMemo(
    () =>
      buildWebHeaderBreadcrumbs(pathname, breadcrumbs, isTabRoute, {
        home: t("common.home"),
        properties: t("properties.myProperties"),
        propertyDetails: t("propertyDetails.title"),
        payments: t("payments.title"),
        profile: t("profile.myProfile"),
      }),
    [pathname, breadcrumbs, t, isTabRoute],
  );

  if (Platform.OS !== "web") {
    return null;
  }

  const displayName = resolveWebHeaderDisplayName(
    user,
    isRTL,
    t("welcome.welcomeTo"),
  );
  const userEmail = resolveWebHeaderEmail(user);
  // ADHA: show application number under the name. Non-ADHA: keep showing email.
  const profileSubtitle = getWebHeaderProfileSubtitle(
    isAdhaJourney,
    adhaApplicationNumber,
    userEmail,
  );

  const handleCopyApplicationNumber = async () => {
    if (!isAdhaJourney || !hasTextValue(adhaApplicationNumber)) {
      return;
    }
    try {
      await Clipboard.setStringAsync(String(adhaApplicationNumber));
      setToastMessage(t("profile.applicationNumberCopied"));
      setToastVisible(true);
    } catch (error) {
      logger.error("WebHeader:Failed to copy application number", error);
      setToastMessage(t("profile.applicationNumberCopyFailed"));
      setToastVisible(true);
    }
  };

  const handleBackPress = () => {
    if (onBackPress) {
      onBackPress();
      return;
    }
    router.back();
  };

  const handleBreadcrumbPress = (item: BreadcrumbItem) => {
    if (!item.path) {
      return;
    }
    // Open this crumb's route. replace()/back() on web shrinks history and
    // Expo syncs that with history.go(), which shows the previous page
    // (for example loan details) instead of Home.
    router.push(item.path as never);
  };

  const handleProfilePress = () => {
    if (!profileButtonRef.current) {
      return;
    }
    profileButtonRef.current.measureInWindow((x, y, width, height) => {
      setProfileButtonPosition({ x: x + width, y: y + height });
      setDropdownVisible(true);
    });
  };

  const closeDropdown = () => setDropdownVisible(false);
  const hideToast = () => setToastVisible(false);
  const titleVariant = isMobile ? "title2" : "largeTitle";
  const breadcrumbVariant = isMobile ? "footnote" : "body";
  const breadcrumbChevronSize = isMobile ? 14 : 16;
  const backIconSize = isMobile ? 28 : 34;
  const chrome = (
    <WebHeaderChrome
      profileButtonPosition={profileButtonPosition}
      dropdownVisible={dropdownVisible}
      onCloseDropdown={closeDropdown}
      toastVisible={toastVisible}
      toastMessage={toastMessage}
      onHideToast={hideToast}
    />
  );
  const profileButton = (
    <WebHeaderProfileChip
      showProfileDetails={showProfileDetails}
      isAdhaJourney={isAdhaJourney}
      isMobile={isMobile}
      isRTL={isRTL}
      displayName={displayName}
      profileSubtitle={profileSubtitle}
      fontPrimary={colors.fontPrimary}
      backgroundColor={colors.backgroundLevel1}
      borderColor={colors.outlineInactive}
      copyIconColor={colors.fontTertiary}
      copyAccessibilityLabel={t("profile.copyApplicationNumber")}
      profileButtonRef={profileButtonRef}
      onProfilePress={handleProfilePress}
      onCopy={handleCopyApplicationNumber}
    />
  );
  const breadcrumbRow = (
    <WebHeaderBreadcrumbRow
      items={autoBreadcrumbs}
      isRTL={isRTL}
      chevronSize={breadcrumbChevronSize}
      chevronColor={colors.fontSecondary}
      variant={breadcrumbVariant}
      onPress={handleBreadcrumbPress}
    />
  );

  if (!isMobile) {
    return (
      <WebHeaderDesktopLayout
        headerPadX={headerPadX}
        title={title}
        isRTL={isRTL}
        shouldShowBackButton={shouldShowBackButton}
        onBackPress={handleBackPress}
        backIconColor={colors.fontPrimary}
        profileButton={profileButton}
        breadcrumbRow={breadcrumbRow}
        chrome={chrome}
      />
    );
  }

  if (isPhone && hasBreadcrumbs) {
    return (
      <WebHeaderPhoneBreadcrumbLayout
        headerPadX={headerPadX}
        phoneBreadcrumbBelow
        profileButton={profileButton}
        breadcrumbRow={breadcrumbRow}
        chrome={chrome}
      />
    );
  }

  return (
    <WebHeaderCompactLayout
      headerPadX={headerPadX}
      title={title}
      isPhone={isPhone}
      shouldShowBackButton={shouldShowBackButton}
      onBackPress={handleBackPress}
      backIconColor={colors.fontPrimary}
      backIconSize={backIconSize}
      titleVariant={titleVariant}
      profileButton={profileButton}
      breadcrumbRow={breadcrumbRow}
      chrome={chrome}
    />
  );
}

const styles = StyleSheet.create({
  desktopContainer: {
    width: "100%",
    // Top inset lives on logo + profile (not the shell) so both share equal space from the top.
    paddingTop: 0,
    paddingBottom: 4,
    gap: 20,
    backgroundColor: "transparent",
  },
  topRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  /** Logo cluster: same top inset as profile; logo hugs the top of this box. */
  desktopLeading: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 24,
    paddingTop: 10,
  },
  /** Match `desktopLeading` top inset so the chip lines up with the logo. */
  desktopProfile: {
    paddingTop: 10,
    flexShrink: 0,
  },
  bottomRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
  },
  header: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 32,
    gap: 16,
    minHeight: 80,
    backgroundColor: "transparent",
  },
  headerMobile: {
    alignItems: "center",
    paddingTop: 16,
    paddingBottom: 20,
    minHeight: 56,
  },
  headerMobileWithBreadcrumbBelow: {
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "space-between",
    minHeight: 0,
    paddingBottom: 16,
  },
  /** Logo + breadcrumb share the profile chip’s height; tops align. */
  mobileLogoBreadcrumbRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  mobileLogoBreadcrumbColumn: {
    flex: 1,
    minWidth: 0,
    /** Match `profileButtonMobile` minHeight so tops + bottoms line up. */
    minHeight: 48,
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  mobileTopRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexGrow: 0,
    flexShrink: 0,
  },
  mobileBreadcrumbRow: {
    alignItems: "flex-start",
    flexGrow: 0,
  },
  leftSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 28,
    flexShrink: 0,
    flex: 1,
  },
  logoContainer: {
    width: 140,
    justifyContent: "flex-start",
    alignItems: "flex-start",
    // SVG wordmark has empty space above the “B”; nudge so ink lines up with profile chip top.
    marginTop: -6,
  },
  logoContainerMobile: {
    justifyContent: "flex-start",
    alignItems: "flex-start",
    flexShrink: 0,
  },
  titleBreadcrumbContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 0,
    flex: 1,
    minHeight: 64,
  },
  titleBreadcrumbContainerMobile: {
    alignItems: "flex-start",
    justifyContent: "center",
    minHeight: 0,
  },
  titleText: {
    textAlign: "left",
  },
  backButton: {
    paddingLeft: 0,
    paddingRight: 4,
    marginRight: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  backButtonMobile: {
    paddingTop: 6,
  },
  centerSection: {
    flex: 1,
    alignItems: "flex-start",
    gap: 12,
    minWidth: 0,
    justifyContent: "center",
  },
  breadcrumbs: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  breadcrumbLink: {
    textDecorationLine: "underline",
  },
  breadcrumbText: {
    textDecorationLine: "none",
  },
  rightSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 24,
    flexShrink: 0,
  },
  profileButton: {
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderWidth: 1,
    minHeight: 64,
    justifyContent: "center",
  },
  profileButtonMobile: {
    minHeight: 48,
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  profileContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  profileInfo: {
    gap: 4,
  },
  profileInfoFlex: {
    flex: 1,
    minWidth: 0,
  },
  subtitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingTop: 2,
  },
  copyButton: {
    width: COPY_ICON_SIZE,
    height: COPY_ICON_SIZE,
    justifyContent: "center",
    alignItems: "center",
  },
  // Kept mounted so the row width stays stable when the icon appears on hover.
  copyButtonHidden: {
    opacity: 0,
    pointerEvents: "none",
  },
});

export default WebHeader;
